import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { api, mensajeError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import FirmaDedo from '../components/FirmaDedo';
import { Loader2, HeartPulse, CheckCircle2, Camera, AlertCircle, RefreshCw } from 'lucide-react';
import {
  useTelefonoClinica, tipoDeFallo, respuestaPerdida, cuandoReintentar, aQuienLlamar, fotoRechazada,
} from '../lib/enlacePaciente';
import ejemploFrontal from '../assets/ejemplo_foto_frontal.jpg';
import ejemploOblicua from '../assets/ejemplo_foto_oblicua.jpg';

// Longitudes máximas = las del backend (salud_router.SaludBody): 1000 en los
// detalles y 2000 en «qué mejorar», para que el 422 no llegue a producirse
// (revisión de enlaces 2026-09-07).
const MAX_DETALLE = 1000;
const MAX_MEJORAR = 2000;

// Las dos fotos que pedimos, con su ejemplo (2026-08-20): van
// directas a la galería «antes» de la historia del paciente. Se piden salvo
// que la CITA de este enlace sea una revisión sucesiva o quirófano, que al
// paciente ya se le fotografió aquí (2026-09-15). Lo
// contesta el servidor en `pide_fotos`, mirando la cita que viaja en la URL.
const FOTOS = [
  { vista: 'frontal', titulo: '1 · De frente', ejemplo: ejemploFrontal,
    ayuda: 'Mirando a la cámara, cara centrada, buena luz.' },
  { vista: 'oblicua', titulo: '2 · Girada (45°)', ejemplo: ejemploOblicua,
    ayuda: 'Media vuelta hacia un lado, como en el ejemplo.' },
];

/**
 * Cuestionario de salud desde casa (público, con token de un solo uso).
 * El paciente nuevo lo recibe por WhatsApp en un mensaje aparte, tras la
 * confirmación o el recordatorio de su cita; al enviar, sus respuestas se
 * vuelcan solas en su ficha (2026-07-25).
 *
 * Envío (2026-09-08, punto 13): los pacientes veían «no se pudo
 * enviar» aunque las respuestas SÍ llegaban, y pulsaban Enviar una y otra
 * vez. La causa está en el camino de vuelta del móvil (respuesta perdida,
 * pestaña en segundo plano, vista web de WhatsApp): el servidor guardaba,
 * la promesa fallaba y cada reintento chocaba con «ya fue enviado», que se
 * pintaba en rojo. Ahora:
 *   - el envío correcto enseña la pantalla de confirmación y un toast;
 *   - el reenvío de un cuestionario ya recibido (`ya_enviado` del backend)
 *     es la pantalla «ya recibido», no error (un 409 o un 410, desde el
 *     23/9/2026, se comprueban antes: ver más abajo);
 *   - si el envío se queda sin respuesta, antes de dar el error se pregunta
 *     al servidor si llegó, y si llegó se enseña «ya recibido»;
 *   - Enviar no se puede pulsar dos veces mientras hay un envío en curso.
 *
 * Abrirlo (23/9/2026: que no haya «rotura de conexión»): cualquier
 * fallo del GET se contaba como «Este enlace no es válido». Un paciente con
 * mala cobertura, o todos a la vez si el servidor se reiniciaba, leía que su
 * enlace estaba muerto y no volvía a probar. Ahora solo 404 y 410 dicen eso;
 * sin respuesta, 5xx o timeout es «No hemos podido conectar con la clínica»
 * con un botón de reintentar; y siempre con el teléfono de la clínica. Lo
 * mismo que ya hacían mis pasos, confirmar cita y la LOPD
 * (lib/enlacePaciente.js).
 *
 * Segunda pasada del 23/9/2026:
 *   - «Procesando» no es «ya recibido». Con otro envío del mismo cuestionario
 *     a medias, el servidor contestaba `ya_enviado` y aquí se enseñaba «ya
 *     recibido» sin que hubiera llegado nada; si ese envío reventaba, la
 *     paciente se iba sin sus alergias en la ficha. Ahora el servidor
 *     contesta 409 y aquí SE PREGUNTA antes de decir que llegó: el criterio
 *     de la LOPD de casa del 21/9 (un error no se enseña como dato). Un 410
 *     tampoco basta.
 *   - Sin teléfono en ningún sitio no se inventa uno: «llama a la clínica».
 */

const BLOQUES = [
  {
    campo: 'alergias',
    texto: '¿Tiene alergia o intolerancia a algún medicamento?',
    detalle: '¿A qué medicamento(s)? *',
    placeholder: 'Ej.: penicilina, ibuprofeno…',
  },
  {
    campo: 'medicacion',
    texto: '¿Toma alguna medicación habitualmente?',
    detalle: '¿Qué medicación toma? *',
    placeholder: 'Ej.: Sintrom, omeprazol 20 mg…',
  },
  {
    campo: 'hialuronico',
    texto: '¿Se ha puesto alguna vez ácido hialurónico en ojeras o pómulos?',
    detalle: '¿Dónde y cuándo? *',
    placeholder: 'Ej.: ojeras, hace 2 años en otra clínica…',
  },
  {
    campo: 'cirugias',
    texto: '¿Ha entrado alguna vez en quirófano?',
    detalle: '¿Qué cirugías? *',
    placeholder: 'Ej.: apendicitis en 2010, cesárea…',
  },
];

export default function PreSaludPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  // La cita para la que se mandó este enlace (`/salud/<token>?cita=<id>`): es
  // lo único que decide si se le piden las fotos, y por eso viaja en la URL y
  // no dentro del enlace — el enlace es uno por paciente y no caduca, así que
  // la última cita que pasara por él le cambiaría el formulario a las demás
  // (2026-09-15). Sin ella se piden, que es el lado seguro.
  const cita = params.get('cita') || '';
  const urlEnlace = `/api/public/salud/${token}`
    + (cita ? `?cita=${encodeURIComponent(cita)}` : '');
  // cargando | listo | enviado (ahora mismo) | ya_recibido (ya constaba) |
  // y, si no se puede abrir: invalido (404/410) | incompleto (422, o sin
  // token en la URL) | espera (429) | conexion (sin respuesta, 5xx…).
  // Sin token no hay nada que preguntar: `/salud/` cortado al copiarlo iba
  // al login del personal (23/9/2026).
  const [estado, setEstado] = useState(token ? 'cargando' : 'incompleto');
  // El 429 al abrir, para decir cuándo volver a intentarlo.
  const [falloCarga, setFalloCarga] = useState(null);
  // Cada «Reintentar» vuelve a lanzar la carga.
  const [intento, setIntento] = useState(0);
  const [nombre, setNombre] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  // El error de `error` es de conexión (o no se sabe si llegó): va con el
  // teléfono debajo. Los de validación («falta el detalle…») no.
  const [errorConTelefono, setErrorConTelefono] = useState(false);
  const [r, setR] = useState({
    alergias: null, alergias_text: '',
    medicacion: null, medicacion_text: '',
    hialuronico: null, hialuronico_text: '',
    cirugias: null, cirugias_text: '',
    mejorar: '',
  });
  // vista → { subiendo, url (preview local), ok }
  const [fotos, setFotos] = useState({ frontal: null, oblicua: null });
  // ¿Le pedimos las fotos? Lo contesta el servidor con el tipo de la cita que
  // va en la URL. Se empieza en `true` y solo un `pide_fotos: false` explícito
  // las quita: si el servidor es de antes del cambio y no manda el campo, el
  // paciente ve lo de siempre en vez de quedarse sin subirlas.
  const [pideFotos, setPideFotos] = useState(true);
  // Error de la última foto: toast + mensaje DENTRO del recuadro «Tus dos
  // fotos» (revisión de enlaces 2026-09-07: antes solo se pintaba al pie,
  // junto a Enviar, y el paciente no lo veía).
  const [fotoError, setFotoError] = useState('');
  const sigRef = useRef(null);
  // Envío en curso: `enviando` (estado) deshabilita el botón, pero entre el
  // clic y el repintado un segundo toque colaría otro POST; la ref lo corta.
  const enviandoRef = useRef(false);
  // El teléfono de la clínica para los avisos: el de Configuración si se
  // puede leer; si no (sin conexión tampoco llega aquel), el último que este
  // dispositivo leyó bien; y si no hay ninguno, «llama a la clínica».
  const telefonoClinica = useTelefonoClinica('', !['cargando', 'listo'].includes(estado) || errorConTelefono);
  const llamanosClinica = aQuienLlamar(telefonoClinica);

  const subirFoto = async (vista, archivo) => {
    if (!archivo) return;
    const url = URL.createObjectURL(archivo);
    setFotoError('');
    setFotos((prev) => ({ ...prev, [vista]: { subiendo: true, url, ok: false } }));
    try {
      const fd = new FormData();
      fd.append('file', archivo);
      fd.append('vista', vista);
      await api.post(`/api/public/salud/${token}/foto`, fd);
      setFotos((prev) => ({ ...prev, [vista]: { subiendo: false, url, ok: true } }));
    } catch (e) {
      setFotos((prev) => ({ ...prev, [vista]: null }));
      // 429: cuándo puede volver a intentarlo. Sin respuesta o 5xx: no se
      // sabe si la foto llegó, y no se le dice que no; volver a subirla es
      // seguro porque el servidor sustituye la anterior (23/9/2026).
      let msg;
      if (e?.response?.status === 429) {
        msg = `Hay mucho movimiento desde tu conexión. Vuelve a intentarlo ${cuandoReintentar(e)}.`;
      } else if (e?.response?.status === 413 || e?.response?.status === 503) {
        // Foto demasiado grande (24/9/2026) o el servidor ocupado con otras:
        // lo que dice el servidor, que va escrito para el paciente; y si el
        // 413 lo corta antes el proxy (sin texto), uno de tú.
        msg = fotoRechazada(e);
      } else if (respuestaPerdida(e)) {
        msg = 'No hemos podido confirmar que la foto se haya subido. Comprueba tu conexión y '
          + 'vuelve a subirla (si ya había llegado, se sustituye).';
      } else {
        msg = mensajeError(e, 'No se pudo subir la foto; inténtalo de nuevo.');
      }
      setFotoError(msg);
      toast.error(msg);
    }
  };

  useEffect(() => {
    if (!token) return undefined;
    let vivo = true;
    setEstado('cargando');
    api.get(urlEnlace)
      .then((res) => {
        if (!vivo) return;
        setNombre(res.data?.nombre || '');
        setPideFotos(res.data?.pide_fotos !== false);
        setEstado(res.data?.estado === 'completado' ? 'ya_recibido' : 'listo');
      })
      .catch((e) => {
        if (!vivo) return;
        // Solo 404/410 dicen que el enlace no vale (ver tipoDeFallo).
        setFalloCarga(e);
        setEstado(tipoDeFallo(e));
      });
    return () => { vivo = false; };
  }, [token, urlEnlace, intento]);

  const reintentar = () => setIntento((n) => n + 1);

  const set = (k, v) => setR((prev) => ({ ...prev, [k]: v }));

  /** ¿Consta ya como recibido en el servidor? (tras un envío sin respuesta)
   *  'si' | 'no' | 'no_se': si tampoco se le puede preguntar, NO es un «no»
   *  (23/9/2026): se le dice que no se sabe, no que no se envió. */
  const yaLlego = async () => {
    try {
      const res = await api.get(urlEnlace);
      return res.data?.estado === 'completado' ? 'si' : 'no';
    } catch {
      return 'no_se';
    }
  };

  const enviar = async () => {
    if (enviandoRef.current) return;
    setError('');
    setErrorConTelefono(false);
    for (const b of BLOQUES) {
      if (r[b.campo] === null) {
        setError('Responde sí o no a todas las preguntas.');
        return;
      }
      if (r[b.campo] === true && !(r[`${b.campo}_text`] || '').trim()) {
        setError(`Falta el detalle: ${b.detalle.replace(' *', '')}`);
        return;
      }
    }
    if (pideFotos && (!fotos.frontal?.ok || !fotos.oblicua?.ok)) {
      setError('Faltan tus dos fotos (de frente y girada 45°).');
      return;
    }
    if (!(r.mejorar || '').trim()) {
      setError('Cuéntanos brevemente qué te gustaría mejorar o tratar.');
      return;
    }
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setError('Firma con el dedo dentro del recuadro antes de enviar.');
      return;
    }
    enviandoRef.current = true;
    setEnviando(true);
    try {
      const signature_b64 = sigRef.current.getTrimmedCanvas().toDataURL('image/png');
      const res = await api.post(`/api/public/salud/${token}`, { ...r, signature_b64 });
      if (res.data?.ya_enviado) {
        // Ya constaba (reintento tras perder la respuesta): no es un error.
        // El servidor solo lo dice con el enlace COMPLETADO (23/9/2026; un
        // envío a medias contesta 409 y se comprueba abajo).
        setEstado('ya_recibido');
        return;
      }
      setEstado('enviado');
      toast.success('Cuestionario enviado correctamente');
    } catch (e) {
      const status = e?.response?.status;
      // 409: otro envío de este cuestionario está a medias; 410: un servidor
      // anterior. NINGUNO dice que llegó (23/9/2026): si ese otro envío
      // revienta, no hay nada en la ficha. Se pregunta, como la LOPD de casa.
      if (status === 409 || status === 410) {
        const llego = await yaLlego();
        if (llego === 'si') {
          setEstado('ya_recibido');
          return;
        }
        setErrorConTelefono(true);
        setError(llego === 'no_se'
          ? 'No hemos podido confirmar si tu cuestionario ha llegado. Comprueba tu conexión y '
            + 'vuelve a pulsar Enviar: si ya había llegado, no se duplica.'
          // Lo que puede durar de verdad: si el otro envío murió sin soltar
          // su reserva, caduca a los 10 minutos (salud_router, 23/9/2026).
          : 'Tu cuestionario todavía no nos consta como recibido: se está guardando o no ha '
            + 'llegado. No cierres esta página y vuelve a pulsar Enviar dentro de unos minutos '
            + '(como mucho, 10): si ya ha llegado, te lo diremos.');
        return;
      }
      // Sin respuesta (red del móvil, tiempo agotado) o error del servidor:
      // antes de dar el envío por fallido se comprueba si llegó.
      if (respuestaPerdida(e)) {
        const llego = await yaLlego();
        if (llego === 'si') {
          setEstado('ya_recibido');
          return;
        }
        setErrorConTelefono(true);
        if (llego === 'no_se') {
          // Ni el envío ni la pregunta han tenido respuesta: no se sabe si
          // llegó. Reenviar es seguro (el servidor no duplica: `ya_enviado`).
          setError('No hemos podido confirmar si tu cuestionario ha llegado. Comprueba tu '
            + 'conexión y vuelve a pulsar Enviar: si ya había llegado, no se duplica.');
          return;
        }
        // Consta que NO llegó. Con un 5xx el fallo fue nuestro, no de su
        // móvil; sin respuesta, lo más probable es su conexión.
        setError(e?.response
          ? 'No se pudo enviar por un fallo nuestro. Vuelve a pulsar Enviar en un momento.'
          : 'No se pudo enviar. Comprueba la conexión de tu móvil e inténtalo de nuevo.');
        return;
      }
      if (status === 429) {
        setError(`Hay mucho movimiento desde tu conexión. Vuelve a intentarlo ${cuandoReintentar(e)}.`);
        return;
      }
      setError(mensajeError(e, 'No se pudo enviar. Comprueba la conexión de tu móvil e inténtalo de nuevo.'));
    } finally {
      enviandoRef.current = false;
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFBF9] flex items-start justify-center px-4 py-8">
      <div className="w-full max-w-lg bg-white rounded-xl border border-border shadow-sm p-5 space-y-4">
        <div className="flex items-center gap-2">
          <HeartPulse className="w-6 h-6 text-[#2E6E5E]" />
          <h1 className="text-xl font-semibold text-[#13231E]">Cuestionario de salud</h1>
        </div>

        {estado === 'cargando' && (
          <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Cargando…
          </div>
        )}

        {/* El enlace no vale (404/410): solo entonces se dice. Con el
            teléfono: «contacta con la clínica» sin número no ayudaba. */}
        {estado === 'invalido' && (
          <div className="text-sm text-muted-foreground py-6 text-center space-y-1" data-testid="salud-invalido">
            <p className="font-medium text-[#13231E]">Este enlace no es válido.</p>
            <p>Si crees que es un error, {llamanosClinica}.</p>
          </div>
        )}

        {/* 422 (el token se cortó al copiarlo) o sin token en la URL. */}
        {estado === 'incompleto' && (
          <div className="text-sm text-muted-foreground py-6 text-center space-y-1" data-testid="salud-incompleto">
            <p className="font-medium text-[#13231E]">Este enlace está incompleto.</p>
            <p>Ábrelo desde el mensaje completo que te enviamos o {llamanosClinica}.</p>
          </div>
        )}

        {/* Sin respuesta, 5xx o timeout (conexion) y el freno de peticiones
            (espera): el enlace sigue siendo suyo y se puede reintentar. */}
        {(estado === 'conexion' || estado === 'espera') && (
          <div className="text-sm text-muted-foreground py-6 text-center space-y-3"
            data-testid={estado === 'espera' ? 'salud-espera' : 'salud-sin-conexion'}>
            {estado === 'espera' ? (
              <>
                <p className="font-medium text-[#13231E]">Ahora mismo hay mucho movimiento.</p>
                <p>
                  Vuelve a intentarlo {cuandoReintentar(falloCarga)}. Tu enlace sigue siendo válido.
                </p>
              </>
            ) : (
              <>
                <p className="font-medium text-[#13231E]">No hemos podido conectar con la clínica.</p>
                <p>Comprueba tu conexión y vuelve a intentarlo. Tu enlace sigue siendo válido.</p>
              </>
            )}
            <Button variant="outline" className="h-11" onClick={reintentar} data-testid="salud-reintentar">
              <RefreshCw className="w-4 h-4 mr-2" /> Volver a intentarlo
            </Button>
            <p>Si sigue sin funcionar, {llamanosClinica}.</p>
          </div>
        )}

        {estado === 'enviado' && (
          <div className="text-center py-8 space-y-2" data-testid="salud-gracias">
            <CheckCircle2 className="w-10 h-10 text-[#2E6E5E] mx-auto" />
            <p className="font-medium text-[#13231E]">¡Gracias{nombre ? `, ${nombre}` : ''}!</p>
            <p className="text-sm font-medium text-[#2E6E5E]" data-testid="salud-enviado-ok">
              Tu cuestionario se ha enviado correctamente.
            </p>
            <p className="text-sm text-muted-foreground">
              Tus respuestas han quedado registradas en tu ficha. No hace falta
              que hagas nada más. Nos vemos en tu cita.
            </p>
          </div>
        )}

        {estado === 'ya_recibido' && (
          <div className="text-center py-8 space-y-2" data-testid="salud-ya-recibido">
            <CheckCircle2 className="w-10 h-10 text-[#2E6E5E] mx-auto" />
            <p className="font-medium text-[#13231E]">¡Gracias{nombre ? `, ${nombre}` : ''}!</p>
            <p className="text-sm font-medium text-[#2E6E5E]">
              Tu cuestionario ya nos había llegado.
            </p>
            <p className="text-sm text-muted-foreground">
              Tus respuestas están registradas en tu ficha: no hace falta que
              lo envíes de nuevo. Nos vemos en tu cita.
            </p>
          </div>
        )}

        {estado === 'listo' && (
          <>
            <p className="text-sm text-muted-foreground">
              {nombre ? `Hola, ${nombre}. ` : ''}Cuatro preguntas rápidas para que
              {pideFotos ? ' tu primera visita ' : ' tu cita '}empiece con todo
              listo. Tus respuestas van directamente a tu ficha clínica.
            </p>

            {BLOQUES.map((b) => (
              <div key={b.campo} className="border border-border rounded-lg p-3 space-y-2"
                data-testid={`salud-${b.campo}`}>
                <div className="text-sm font-medium text-[#13231E]">{b.texto}</div>
                <div className="flex gap-2">
                  {[['Sí', true], ['No', false]].map(([lbl, val]) => (
                    <button key={lbl} type="button"
                      onClick={() => set(b.campo, val)}
                      className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                        r[b.campo] === val
                          ? 'bg-[#2E6E5E] border-[#2E6E5E] text-white'
                          : 'bg-white border-border text-muted-foreground hover:text-[#13231E]'
                      }`}
                      data-testid={`salud-${b.campo}-${lbl.toLowerCase()}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
                {r[b.campo] === true && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">{b.detalle}</div>
                    <Textarea rows={2} maxLength={MAX_DETALLE} value={r[`${b.campo}_text`]}
                      onChange={(e) => set(`${b.campo}_text`, e.target.value)}
                      placeholder={b.placeholder}
                      data-testid={`salud-${b.campo}-detalle`} />
                  </div>
                )}
              </div>
            ))}

            {/* Sus dos fotos, con ejemplo (van al «antes» de su historia).
                En revisiones sucesivas y quirófano no se piden: `pide_fotos`,
                que el servidor resuelve con la cita de la URL. */}
            {pideFotos && (
            <div className="border border-border rounded-lg p-3 space-y-3" data-testid="salud-fotos">
              <div className="text-sm font-medium text-[#13231E]">Tus dos fotos</div>
              <p className="text-xs text-muted-foreground">
                Haznos llegar dos fotos como las de ejemplo: una de frente y otra
                girada 45°. Buena luz, sin gafas y con el pelo retirado de la cara.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {FOTOS.map((f) => (
                  <div key={f.vista} className="space-y-1.5" data-testid={`salud-foto-${f.vista}`}>
                    <div className="text-xs font-semibold text-[#13231E]">{f.titulo}</div>
                    <div className="relative rounded-md overflow-hidden border border-border bg-[#F2F2F0]">
                      <img src={fotos[f.vista]?.url || f.ejemplo} alt={f.titulo}
                        className="w-full aspect-[3/4] object-cover" />
                      {!fotos[f.vista]?.url && (
                        <span className="absolute top-1 left-1 rounded bg-black/55 text-white text-[10px] px-1.5 py-0.5">
                          EJEMPLO
                        </span>
                      )}
                      {fotos[f.vista]?.subiendo && (
                        <span className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <Loader2 className="w-6 h-6 text-white animate-spin" />
                        </span>
                      )}
                      {fotos[f.vista]?.ok && (
                        <span className="absolute top-1 right-1 rounded-full bg-[#2E6E5E] p-1">
                          <CheckCircle2 className="w-4 h-4 text-white" />
                        </span>
                      )}
                    </div>
                    <label className={`flex items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-xs font-medium cursor-pointer transition-colors ${
                      fotos[f.vista]?.ok
                        ? 'border-[#2E6E5E] text-[#2E6E5E] bg-[rgba(46,110,94,0.06)]'
                        : 'border-[#2E6E5E] bg-[#2E6E5E] text-white hover:bg-[#1E4A3F]'
                    }`}>
                      <Camera className="w-4 h-4" />
                      {fotos[f.vista]?.ok ? 'Cambiar mi foto' : 'Subir mi foto'}
                      <input type="file" accept="image/*" className="hidden"
                        onChange={(e) => { subirFoto(f.vista, e.target.files?.[0]); e.target.value = ''; }}
                        data-testid={`salud-foto-${f.vista}-input`} />
                    </label>
                    <p className="text-[10px] text-muted-foreground leading-tight">{f.ayuda}</p>
                  </div>
                ))}
              </div>
              {fotoError && (
                <p className="text-sm text-red-600 flex items-start gap-1" data-testid="salud-foto-error">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-[2px]" /> {fotoError}
                </p>
              )}
            </div>
            )}

            {/* Qué le gustaría mejorar o tratar */}
            <div className="border border-border rounded-lg p-3 space-y-2" data-testid="salud-mejorar">
              <div className="text-sm font-medium text-[#13231E]">¿Qué te gustaría mejorar o tratar?</div>
              <Textarea rows={3} maxLength={MAX_MEJORAR} value={r.mejorar}
                onChange={(e) => set('mejorar', e.target.value)}
                placeholder="Ej.: lo que te gustaría mejorar y desde cuándo te preocupa…"
                data-testid="salud-mejorar-texto" />
            </div>

            {/* Firma con el dedo, como en la LOPD */}
            <div className="border border-border rounded-lg p-3 space-y-2" data-testid="salud-firma">
              <div className="text-sm font-medium text-[#13231E]">Firma</div>
              <p className="text-xs text-muted-foreground">
                Firma con el dedo o un lápiz táctil dentro del recuadro para
                confirmar que la información es correcta.
              </p>
              <FirmaDedo
                ref={sigRef}
                testid="salud-signature"
                alturaCls="h-40 sm:h-48"
                derecha={<span className="text-xs text-muted-foreground">{new Date().toLocaleDateString('es-ES')}</span>}
              />
            </div>

            {error && <p className="text-sm text-red-600" data-testid="salud-error">{error}</p>}
            {error && errorConTelefono && (
              <p className="text-sm text-muted-foreground" data-testid="salud-error-telefono">
                Si sigue sin funcionar, {llamanosClinica}.
              </p>
            )}

            <Button onClick={enviar} disabled={enviando}
              className="w-full h-11 bg-[#2E6E5E] hover:bg-[#1E4A3F] text-base"
              data-testid="salud-enviar">
              {enviando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Enviar
            </Button>
            <p className="text-[10px] text-muted-foreground text-center">
              Tus datos se tratan conforme al RGPD y solo los ve el equipo de la clínica.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
